import type { LanguageAlternates } from "@/context/LanguageAlternatesProvider";
import { env } from "@/env";
import { i18n } from "@/libs/i18n";

import { isSegmentRewritable } from "../rewriteUrls/isSegmentRewritable";
import { reverseRewriteUrls } from "../rewriteUrls/reverseRewriteUrls";
import { getBlogArticleAlternates } from "../sitemap/blog/getBlogArticleAlternates";
import { getAlternates } from "../sitemap/builder-pages/getAlternates";
import type { CategoryMap, ProductSkuMap } from "../sitemap/magento/getProductAlternates";
import { getCategoryAlternates, getProductAlternates } from "../sitemap/magento/getProductAlternates";

const rewriteUrlsForLanguageSwitch = (url: string, locale: string): string => {
  const rewriteConfig = env.rewriteUrlsConfig[locale];

  const urlsParts = url.split("/");
  const firstSegment = urlsParts[1];

  if (!firstSegment) return url;

  if (isSegmentRewritable(firstSegment)) {
    const rewritten = rewriteConfig?.[firstSegment];
    if (rewritten) {
      return url.replace(firstSegment, rewritten);
    }
  }

  return url;
};

export type PageType = "product" | "category" | "blog-article" | "builder-page" | "static";

export type PageContext = {
  pageType: PageType;
  locale: string;
  urlPath: string;
  sku?: string;
  skuMap?: ProductSkuMap;
  categoryUid?: string;
  categoryMap?: CategoryMap;
  additionalLanguageLinks?: unknown;
  blogSlug?: string;
};

export const getLanguageAlternates = (ctx: PageContext): LanguageAlternates => {
  const { pageType, locale, urlPath } = ctx;
  const alternates: LanguageAlternates = {};

  switch (pageType) {
    case "product": {
      if (ctx.sku && ctx.skuMap) {
        const productAlternates = getProductAlternates(ctx.sku, locale, ctx.skuMap);
        processAlternatesFromSitemap(productAlternates, alternates);
      }
      break;
    }

    case "category": {
      if (ctx.categoryUid && ctx.categoryMap) {
        const categoryAlternates = getCategoryAlternates(ctx.categoryUid, locale, ctx.categoryMap);
        processAlternatesFromSitemap(categoryAlternates, alternates);
      }
      break;
    }

    case "blog-article": {
      if (ctx.additionalLanguageLinks && ctx.blogSlug) {
        const blogAlternates = getBlogArticleAlternates(ctx.additionalLanguageLinks, ctx.blogSlug);
        processAlternatesFromSitemap(blogAlternates, alternates);
      }
      break;
    }

    case "builder-page": {
      if (ctx.additionalLanguageLinks) {
        const builderAlternates = getAlternates(ctx.additionalLanguageLinks);
        processAlternatesFromSitemap(builderAlternates, alternates);
      }
      break;
    }

    case "static":
    default: {
      const canonicalPath = reverseRewriteUrls(urlPath, locale);

      for (const altLocale of i18n.locales) {
        if (altLocale !== locale) {
          alternates[altLocale] = rewriteUrlsForLanguageSwitch(canonicalPath, altLocale);
        }
      }
      break;
    }
  }

  if (Object.keys(alternates).length === 0) {
    const canonicalPath = reverseRewriteUrls(urlPath, locale);

    for (const altLocale of i18n.locales) {
      if (altLocale !== locale) {
        const rewrittenPath = rewriteUrlsForLanguageSwitch(canonicalPath, altLocale);

        const noRewriteFound = rewrittenPath === canonicalPath;
        const isBuilderOrBlogPage = pageType === "builder-page" || pageType === "blog-article";

        if (isBuilderOrBlogPage && noRewriteFound && urlPath !== "/") {
          alternates[altLocale] = "/";
        } else {
          alternates[altLocale] = rewrittenPath;
        }
      }
    }
  }

  return alternates;
};

const extractPathFromUrl = (fullUrl: string, locale: string): string | undefined => {
  try {
    const url = new URL(fullUrl);
    const pathname = url.pathname;
    const localePrefix = `/${locale}`;

    if (pathname.startsWith(localePrefix)) {
      return pathname.slice(localePrefix.length) || "/";
    }

    return pathname;
  } catch {
    return undefined;
  }
};

const processAlternatesFromSitemap = (
  sitemapAlternates:
    | {
        languages?: Record<string, string | undefined> | Record<string, string> | undefined;
      }
    | undefined,
  alternates: LanguageAlternates
): void => {
  if (!sitemapAlternates?.languages) return;

  for (const [altLocale, fullUrl] of Object.entries(sitemapAlternates.languages)) {
    if (fullUrl) {
      const path = extractPathFromUrl(fullUrl, altLocale);
      if (path) {
        alternates[altLocale] = path;
      }
    }
  }
};
